import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { QRCodeCanvas } from "qrcode.react";
import { CurrencyCircleDollar, Copy, CheckCircle, Clock, Warning } from "@phosphor-icons/react";

const fmtBRL = (cents) => `R$ ${(cents / 100).toFixed(2).replace(".", ",")}`;

export default function PixCheckoutDialog({ open, onOpenChange, planKey, planLabel, amount }) {
  const [busy, setBusy] = useState(false);
  const [pix, setPix] = useState(null);
  const [status, setStatus] = useState("idle"); // idle | pending | paid | error
  const [error, setError] = useState(null);
  const pollRef = useRef(null);

  const create = async () => {
    setBusy(true); setError(null);
    try {
      const { data } = await api.post("/payments/pix/create", { lookup_key: planKey });
      setPix(data);
      setStatus("pending");
    } catch (e) {
      setError(e.response?.data?.detail || "Erro ao gerar PIX");
      setStatus("error");
    } finally { setBusy(false); }
  };

  // Poll status every 4s while pending
  useEffect(() => {
    if (!open) return;
    if (status !== "pending") return;
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await api.get("/payments/pix/status");
        if (data.subscription_active) {
          setStatus("paid");
          clearInterval(pollRef.current);
          toast.success("Pagamento confirmado! Assinatura ativa 🎉");
        }
      } catch {}
    }, 4000);
    return () => clearInterval(pollRef.current);
  }, [open, status]);

  useEffect(() => {
    if (open && !pix) create();
    if (!open) { setPix(null); setStatus("idle"); setError(null); }
    // eslint-disable-next-line
  }, [open]);

  const copy = async () => {
    if (!pix?.qr_code_txt) return;
    try {
      await navigator.clipboard.writeText(pix.qr_code_txt);
      toast.success("Código PIX copiado!");
    } catch { toast.error("Copie o código manualmente"); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="pix-dialog" className="max-w-md">
        <DialogHeader>
          <DialogTitle className="text-2xl flex items-center gap-2" style={{ fontFamily: "Manrope" }}>
            <CurrencyCircleDollar size={28} weight="duotone" className="text-emerald-600" /> Pagar com PIX
          </DialogTitle>
          <DialogDescription>{planLabel} — {fmtBRL(amount)}</DialogDescription>
        </DialogHeader>

        {busy && (
          <div className="py-10 text-center text-slate-500">Gerando código PIX…</div>
        )}

        {status === "error" && (
          <div className="rounded-lg border-2 border-amber-300 bg-amber-50 p-4 space-y-2">
            <div className="flex items-center gap-2 text-amber-800 font-bold">
              <Warning size={20} weight="fill" /> Não foi possível gerar o PIX
            </div>
            <p className="text-sm text-amber-900">{error}</p>
            <Button onClick={create} className="w-full mt-2 bg-blue-600 hover:bg-blue-700 hover:text-white text-white">Tentar novamente</Button>
          </div>
        )}

        {status === "paid" && (
          <div className="text-center py-8 space-y-3">
            <CheckCircle size={72} weight="fill" className="mx-auto text-emerald-500" />
            <h3 className="text-2xl font-extrabold text-emerald-700">Pagamento confirmado!</h3>
            <p className="text-slate-600">Sua assinatura está ativa. Próximo vencimento: em 30 dias.</p>
            <Button onClick={() => onOpenChange(false)} className="bg-emerald-600 hover:bg-emerald-700 hover:text-white text-white">Voltar ao app</Button>
          </div>
        )}

        {status === "pending" && pix && (
          <div className="space-y-4">
            <div className="flex justify-center bg-white p-3 rounded-lg border-2 border-emerald-200">
              {pix.qr_svg_url ? (
                <img src={pix.qr_svg_url} alt="PIX QR" className="w-56 h-56" data-testid="pix-qr-img" />
              ) : pix.qr_code_txt ? (
                <QRCodeCanvas value={pix.qr_code_txt} size={220} level="M" fgColor="#059669" data-testid="pix-qr-canvas" />
              ) : null}
            </div>

            <div className="flex items-center gap-2 rounded-lg bg-blue-50 border border-blue-200 p-3">
              <Clock size={20} className="text-blue-700 shrink-0" />
              <p className="text-xs text-blue-900">
                Escaneie o QR no app do seu banco <strong>OU</strong> copie e cole o código abaixo. O status atualiza sozinho.
              </p>
            </div>

            {pix.qr_code_txt && (
              <div className="flex gap-2">
                <input
                  readOnly
                  value={pix.qr_code_txt}
                  className="flex-1 text-[10px] bg-slate-50 border rounded px-2 py-2 font-mono"
                  data-testid="pix-copy-code"
                />
                <Button variant="outline" onClick={copy} data-testid="btn-copy-pix"><Copy size={14} /></Button>
              </div>
            )}

            <p className="text-center text-xs text-slate-500">Aguardando confirmação do pagamento…</p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
